import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Clock, Award } from 'lucide-react';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';
import type { LearningPath, CheckpointScore } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';

interface YourJourneyProps {
  paths: LearningPath[];
  content: ContentDocument[];
  userSince: number | string | null;
}

interface Point {
  weekStart: number;
  mastered: number;
}

const MS_WEEK = 7 * 24 * 60 * 60 * 1000;

function startOfWeekUTC(ts: number): number {
  const d = new Date(ts);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.getTime();
}

function formatMinutes(total: number): string {
  if (!total || total < 1) return '—';
  const hours = Math.floor(total / 60);
  const mins = Math.round(total % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function daysSince(iso: number | string | null): number | null {
  if (!iso) return null;
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000));
  return days >= 0 ? days : null;
}

export function YourJourney({ paths, content, userSince }: YourJourneyProps) {
  const stats = useMemo(() => {
    let conceptsSeen = 0;
    let conceptsMastered = 0;
    let totalMinutes = 0;
    const masteryEvents: Array<{ ts: number; score: number }> = [];

    for (const path of paths) {
      conceptsSeen += path.nodes?.length ?? 0;
      totalMinutes += path.progress?.totalTimeSpent ?? 0;
      const scores = (path.progress?.checkpointScores ?? {}) as Record<string, CheckpointScore>;
      for (const cp of Object.values(scores)) {
        const ts = cp.timestamp ? Date.parse(cp.timestamp) : NaN;
        if (!Number.isNaN(ts)) {
          masteryEvents.push({ ts, score: cp.score });
          if (cp.score >= 80) conceptsMastered += 1;
        }
      }
    }

    // Build trajectory — weekly cumulative count of mastered concepts for the last 8 weeks.
    const now = startOfWeekUTC(Date.now());
    const weeks: Point[] = [];
    const running = new Map<number, number>();
    for (const evt of masteryEvents) {
      if (evt.score < 80) continue;
      const w = startOfWeekUTC(evt.ts);
      running.set(w, (running.get(w) ?? 0) + 1);
    }
    let cumulative = 0;
    // Sum everything before the 8-week window
    const windowStart = now - 7 * MS_WEEK;
    for (const [w, n] of running) {
      if (w < windowStart) cumulative += n;
    }
    for (let i = 7; i >= 0; i--) {
      const w = now - i * MS_WEEK;
      cumulative += running.get(w) ?? 0;
      weeks.push({ weekStart: w, mastered: cumulative });
    }

    return {
      conceptsSeen,
      conceptsMastered,
      totalMinutes,
      uploads: content.length,
      weeks,
    };
  }, [paths, content]);

  const days = daysSince(userSince);
  const hasAny = stats.conceptsSeen > 0 || stats.totalMinutes > 0 || stats.uploads > 0;

  if (!hasAny) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${THICK_TILE_CLASSNAME} p-5 h-full flex flex-col`}
        style={THICK_TILE_STYLE}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.03] to-gold-500/[0.03] pointer-events-none" />
        <div className="relative flex items-center gap-2 mb-1">
          <Sparkles size={14} className="text-indigo-400" />
          <h3 className="font-heading font-bold text-base m-0">Your Journey</h3>
        </div>
        <p className="relative text-xs text-content-muted mb-6 font-body">
          Track your weekly mastery, time spent, and concepts learned over time.
        </p>
        <div className="relative flex-1 flex flex-col items-center justify-center text-center py-6">
          <div className="text-sm font-body text-content-secondary mb-1">No journey data yet</div>
          <p className="text-[12px] text-content-dim font-body max-w-xs">
            Upload content and complete a few learning sections to start tracking your progress here.
          </p>
        </div>
      </motion.section>
    );
  }

  const max = Math.max(1, ...stats.weeks.map((w) => w.mastered));

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${THICK_TILE_CLASSNAME} p-5 h-full`}
      style={THICK_TILE_STYLE}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.03] to-gold-500/[0.03] pointer-events-none" />

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-gold-500" />
            <h2 className="font-heading font-bold text-base m-0">Your Journey</h2>
          </div>
          {days !== null && days > 0 && (
            <span className="text-xs text-content-muted font-body">
              {days} day{days === 1 ? '' : 's'} with CogniPrint
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard
            icon={<Award size={14} />}
            label="Concepts mastered"
            value={stats.conceptsMastered}
            color="#F0A500"
          />
          <StatCard
            icon={<TrendingUp size={14} />}
            label="Concepts covered"
            value={stats.conceptsSeen}
            color="#6C5CE7"
          />
          <StatCard
            icon={<Clock size={14} />}
            label="Time learning"
            value={formatMinutes(stats.totalMinutes)}
            color="#0ABFBC"
          />
          <StatCard
            icon={<Sparkles size={14} />}
            label="Uploads"
            value={stats.uploads}
            color="#A594F9"
          />
        </div>

        {/* 8-week trajectory */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-content-muted font-body">Mastered concepts — last 8 weeks</span>
            <span className="text-[10px] font-mono text-content-dim">{stats.weeks[0].mastered} → {stats.weeks[stats.weeks.length - 1].mastered}</span>
          </div>
          <div className="flex items-end gap-1.5 h-16">
            {stats.weeks.map((w, i) => {
              const heightPct = (w.mastered / max) * 100;
              const isLatest = i === stats.weeks.length - 1;
              return (
                <div key={w.weekStart} className="flex-1 flex flex-col justify-end">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${heightPct}%` }}
                    transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    className={`rounded-t-sm ${isLatest ? 'bg-gradient-to-t from-gold-500 to-gold-300' : 'bg-indigo-500/60'}`}
                    style={{ minHeight: 2 }}
                  />
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-content-dim font-body mt-2">
            {stats.conceptsMastered === 0
              ? "Complete your first checkpoint to start your trajectory."
              : stats.weeks[stats.weeks.length - 1].mastered > stats.weeks[0].mastered
                ? 'Keep going — the curve bends your way when you stay consistent.'
                : "You've got this foundation. Pick a concept and push deeper this week."
            }
          </p>
        </div>
      </div>
    </motion.section>
  );
}

function StatCard({
  icon, label, value, color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="rounded-lg bg-dark-subtle border border-dark-border p-3">
      <div className="flex items-center gap-1.5 mb-1.5 text-content-muted" style={{ color }}>
        {icon}
        <span className="text-[10px] font-body uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="font-heading font-bold text-xl text-content leading-none">{value}</div>
    </div>
  );
}
